import { Box, Grid, TextField, Typography } from '@mui/material'
import { CountrySelector } from 'components/Common/CountrySelector/CountrySelector'
import { RegionSelector } from 'components/Common/CountrySelector/RegionSelector'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import _ from 'lodash'
// import { TextField } from 'mui-rff'
import { HTMLProps } from 'react'
import { Field, FieldMetaState, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import {
  AddressDto,
  CountryDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { showFieldErrors } from 'utils/react-final-form-utils'
import './AddressForm.scss'

type Props = {
  address: AddressDto
  formTitle?: string
  variant: 'outlined' | 'filled' | 'standard'
  loading: boolean
  onChangeAddress: (address: AddressDto) => void | Promise<void>
  meta?: FieldMetaState<unknown>
  disableAutocomplete?: boolean
} & HTMLProps<HTMLDivElement>

export const AddressForm = (props: Props) => {
  const {
    address,
    onChangeAddress,
    loading,
    formTitle,
    variant,
    meta,
    disableAutocomplete,
    disabled,
    required,
    className,
    ...divProps
  } = props

  const { t } = useTranslation()
  const streetAutocomplete = props.disableAutocomplete ? 'off' : 'street'
  const houseNumberAutocomplete = props.disableAutocomplete
    ? 'off'
    : 'houseNumber'
  const houseNumberAdditionAutocomplete = props.disableAutocomplete
    ? 'off'
    : 'houseNumberAddition'
  const zipCodeAutocomplete = props.disableAutocomplete ? 'off' : 'zipCode'
  const countryAutocomplete = props.disableAutocomplete ? 'off' : 'country'
  const stateOrProvinceAutocomplete = props.disableAutocomplete
    ? 'off'
    : 'stateOrProvince'
  const cityAutocomplete = props.disableAutocomplete ? 'off' : 'city'

  const handleSubmit = (address: AddressDto) => {
    if (!_.isEqual(address, props.address)) {
      onChangeAddress(address)
    }
  }

  return (
    <div {...divProps} className={`${props.className}`}>
      {formTitle && (
        <Box marginBottom={2}>
          <Typography variant="h6">{formTitle}</Typography>
        </Box>
      )}
      <Form
        initialValues={address}
        onSubmit={handleSubmit}
        // validate={validateForm}
      >
        {(formProps) => {
          return (
            <div className="">
              {/* <AutoSaveWrapper debounce={0} save={formProps.handleSubmit} /> */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    onFocus={(e) => e.target.select()}
                    name="street"
                    label={
                      <LocalizedTypography translationKey="common:address-street">
                        street
                      </LocalizedTypography>
                    }
                    disabled={props.loading || props.disabled}
                    variant={props.variant}
                    // required={props.required}
                    autoComplete={streetAutocomplete}
                    value={formProps?.values?.street || ''}
                    onChange={(e) =>
                      formProps.form.change('street', e.target.value)
                    }
                    onBlur={() => formProps.handleSubmit()}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    onFocus={(e) => e.target.select()}
                    fullWidth
                    name="houseNumber"
                    value={formProps?.values?.houseNumber || ''}
                    onChange={(e) =>
                      formProps.form.change('houseNumber', e.target.value)
                    }
                    onBlur={() => formProps.handleSubmit()}
                    label={
                      <LocalizedTypography translationKey="common:address-house-number">
                        house number
                      </LocalizedTypography>
                    }
                    disabled={props.loading || props.disabled}
                    variant={props.variant}
                    autoComplete={houseNumberAutocomplete}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    onFocus={(e) => e.target.select()}
                    fullWidth
                    name="houseNumberAddition"
                    value={formProps?.values?.houseNumberAddition || ''}
                    onBlur={() => formProps.handleSubmit()}
                    onChange={(e) =>
                      formProps.form.change(
                        'houseNumberAddition',
                        e.target.value,
                      )
                    }
                    label={
                      <LocalizedTypography translationKey="common:address-house-number-addition">
                        addition
                      </LocalizedTypography>
                    }
                    disabled={props.loading || props.disabled}
                    variant={props.variant}
                    autoComplete={houseNumberAdditionAutocomplete}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    onFocus={(e) => e.target.select()}
                    fullWidth
                    name="zipCode"
                    value={formProps?.values?.zipCode || ''}
                    onChange={(e) =>
                      formProps.form.change('zipCode', e.target.value)
                    }
                    label={
                      <LocalizedTypography translationKey="common:address-zipcode">
                        zip code
                      </LocalizedTypography>
                    }
                    variant={props.variant}
                    disabled={props.loading || props.disabled}
                    autoComplete={zipCodeAutocomplete}
                    onBlur={() => formProps.handleSubmit()}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field<CountryDto> name="country">
                    {(fieldProps) => (
                      <CountrySelector
                        selectedCountryCode={fieldProps.input.value.code || ''}
                        onChange={(country) => {
                          fieldProps.input.onChange({
                            name: country.countryName,
                            code: country.countryShortCode,
                          })
                          formProps.form.change('stateOrProvince', undefined)
                          formProps.handleSubmit()
                        }}
                        // onBlur={fieldProps.input.onBlur}
                        onBlur={() => formProps.handleSubmit()}
                        onFocus={fieldProps.input.onFocus}
                        disabled={props.loading || props.disabled}
                        TextFieldProps={{
                          variant: props.variant,
                          required: props.required,
                          helperText: showFieldErrors(fieldProps.meta)
                            ? ''
                            : fieldProps.meta.error,
                          error: Boolean(fieldProps.meta.error),
                          label: t('common:country'),
                          disabled: props.loading,
                          name: 'country',
                          inputProps: {
                            autoComplete: countryAutocomplete,
                          },
                        }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field<string> name="stateOrProvince">
                    {(fieldProps) => {
                      return (
                        <RegionSelector
                          countryCode={formProps?.values?.country?.code || ''}
                          selectedRegionName={fieldProps.input.value}
                          onRegionChange={(region) => {
                            fieldProps.input.onChange(region.name)
                            formProps.handleSubmit()
                          }}
                          disabled={props.loading || props.disabled}
                          onFocus={fieldProps.input.onFocus}
                          // onBlur={fieldProps.input.onBlur}
                          onBlur={() => formProps.handleSubmit()}
                          TextFieldProps={{
                            helperText: showFieldErrors(fieldProps.meta)
                              ? fieldProps.meta.error
                              : '',
                            error: showFieldErrors(fieldProps.meta),
                            label: t('common:state', 'state or province'),
                            variant: props.variant,
                            disabled: props.loading,
                            InputProps: {},
                            InputLabelProps: {
                              shrink: true,
                              disabled: props.loading,
                            },
                            inputProps: {
                              autoComplete: stateOrProvinceAutocomplete,
                            },
                            name: 'stateOrProvince',
                          }}
                        />
                      )
                    }}
                  </Field>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    onFocus={(e) => e.target.select()}
                    fullWidth
                    name="city"
                    onBlur={() => formProps.handleSubmit()}
                    value={formProps?.values?.city || ''}
                    onChange={(e) =>
                      formProps.form.change('city', e.target.value)
                    }
                    label={
                      <LocalizedTypography translationKey="common:city">
                        city
                      </LocalizedTypography>
                    }
                    variant={props.variant}
                    disabled={props.loading || props.disabled}
                    autoComplete={cityAutocomplete}
                  />
                </Grid>
              </Grid>
            </div>
          )
        }}
      </Form>
    </div>
  )
}
